import { Expand } from "lucide-react";
import { useEffect, useState } from "react";
import type { Message } from "../../types/message";
import { ImagePreviewModal } from "../image-preview-modal";
import { MessageBubble } from "./message-bubble";

export default function ImageMessage({ message }: { message: Message }) {
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [isFullscreenOpen, setIsFullscreenOpen] = useState(false);

  useEffect(() => {
    if (message.url) {
      setIsLoading(true);
      setHasError(false);
      setIsFullscreenOpen(false);
    }
  }, [message.url]);

  if (!message.url) return null;

  return (
    <MessageBubble message={message}>
      <div
        className={`group relative inline-block max-w-full overflow-hidden rounded-lg ${
          isLoading ? "min-h-24 min-w-24" : ""
        }`}
      >
        {hasError ? (
          <div className="flex min-h-24 w-full items-center justify-center bg-slate-200 text-xs text-slate-600">
            Imagem indisponível
          </div>
        ) : (
          <>
            <div
              className={`absolute inset-0 bg-slate-200 ${
                isLoading ? "animate-pulse" : "opacity-0"
              }`}
              aria-hidden="true"
            />
            <img
              src={message.url}
              alt={message.contentType || "Imagem"}
              loading="lazy"
              decoding="async"
              onLoad={() => setIsLoading(false)}
              onError={() => {
                setIsLoading(false);
                setHasError(true);
              }}
              className={`max-w-full rounded-lg transition-opacity duration-150 ${
                isLoading ? "opacity-0" : "opacity-100"
              }`}
            />

            <button
              type="button"
              aria-label="Expandir imagem em tela cheia"
              onClick={() => setIsFullscreenOpen(true)}
              className="btn-icon absolute right-2 top-2 z-10 h-8 w-8 bg-slate-900/70 text-white hover:bg-slate-900 focus-visible:ring-slate-300"
            >
              <Expand size={16} />
            </button>
          </>
        )}
      </div>

      <ImagePreviewModal
        isOpen={isFullscreenOpen}
        src={message.url}
        alt={message.contentType || "Imagem"}
        onClose={() => setIsFullscreenOpen(false)}
      />

      {message.caption?.trim() ? (
        <p
          className={`mt-2 whitespace-pre-wrap text-sm leading-snug ${
            message.role === "user" ? "text-slate-700" : "text-slate-100"
          }`}
        >
          {message.caption}
        </p>
      ) : null}
    </MessageBubble>
  );
}
